import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PasswordStrength, { validatePassword } from '../components/PasswordStrength';
import api from '../api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const ROLE_LABELS = {
  care_rep:'Customer Care Rep', ict_staff:'ICT Staff',
  ict_manager:'ICT Manager', branch_manager:'Branch Manager',
  finance_officer:'Finance Officer', super_admin:'Super Admin',
};

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mustChange = searchParams.get('must_change') === 'true' || user?.must_change_password;
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [saving, setSaving] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password:'', new_password:'', confirm_password:'' });
  const [showPw, setShowPw] = useState({ current:false, new:false, confirm:false });
  const [pwSaving, setPwSaving] = useState(false);

  const handleProfileSave = async () => {
    if (!fullName.trim()) return toast.error('Name cannot be empty');
    setSaving(true);
    try {
      await api.put('/api/auth/profile', { full_name: fullName });
      toast.success('Profile updated!');
    } catch (err) { toast.error(err.response?.data?.message || 'Update failed'); }
    finally { setSaving(false); }
  };

  const handlePasswordChange = async () => {
    if (!pwForm.current_password || !pwForm.new_password || !pwForm.confirm_password)
      return toast.error('Please fill all fields');
    if (pwForm.new_password !== pwForm.confirm_password)
      return toast.error('New passwords do not match');
    const pwError = validatePassword(pwForm.new_password);
    if (pwError) return toast.error(pwError);
    if (pwForm.new_password === pwForm.current_password)
      return toast.error('New password cannot be the same as your current password');
    setPwSaving(true);
    try {
      await api.put('/api/auth/change-password', {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success('✅ Password changed successfully!');
      if (mustChange) {
        // Log out and back in to refresh the token without must_change flag
        toast('Redirecting to dashboard…', { duration: 1500 });
        setTimeout(() => {
          // Update session storage user to clear must_change_password
          const savedUser = sessionStorage.getItem('hd_user');
          if (savedUser) {
            const u = JSON.parse(savedUser);
            u.must_change_password = false;
            sessionStorage.setItem('hd_user', JSON.stringify(u));
          }
          navigate('/');
        }, 1500);
      } else {
        setPwForm({ current_password:'', new_password:'', confirm_password:'' });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password');
    } finally { setPwSaving(false); }
  };

  const initials = user?.full_name?.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase();

  return (
    <div style={{ maxWidth:680 }}>
      {/* Force password change banner */}
      {mustChange && (
        <div style={{ background:'#FEF1E8', border:'1px solid var(--orange)', borderLeft:'4px solid var(--orange)', borderRadius:'var(--radius)', padding:'14px 16px', marginBottom:20, display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:24 }}>🔑</span>
          <div>
            <div style={{ fontWeight:600, color:'var(--orange2)', marginBottom:3 }}>Password Change Required</div>
            <div style={{ fontSize:12, color:'var(--text2)', lineHeight:1.5 }}>
              For security, you must change your default password before using the system.
              Your default password cannot be reused after changing it.
            </div>
          </div>
        </div>
      )}

      {/* Profile Header — hidden during forced change */}
      {!mustChange && (
        <div className="card" style={{ marginBottom:16 }}>
          <div className="card-body" style={{ display:'flex', alignItems:'center', gap:20 }}>
            <div style={{
              width:70, height:70, borderRadius:'50%',
              background:'linear-gradient(135deg,var(--sky),var(--orange))',
              color:'#fff', fontSize:24, fontWeight:700,
              display:'flex', alignItems:'center', justifyContent:'center',
              fontFamily:'Sora,sans-serif', flexShrink:0, boxShadow:'var(--shadow-sky)',
            }}>{initials}</div>
            <div>
              <div style={{ fontFamily:'Sora,sans-serif', fontSize:18, fontWeight:600 }}>{user?.full_name}</div>
              <div style={{ fontSize:13, color:'var(--text3)', marginTop:3 }}>{user?.email}</div>
              <div style={{ display:'flex', gap:8, marginTop:8 }}>
                <span className="badge badge-resolved">{ROLE_LABELS[user?.role]}</span>
                <span className="badge badge-in_progress">🏢 {user?.branch}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile — hidden during forced change */}
      {!mustChange && (
        <div className="card" style={{ marginBottom:16 }}>
          <div className="card-body">
            <div style={{ fontFamily:'Sora,sans-serif', fontSize:14, fontWeight:600, marginBottom:16, paddingBottom:12, borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:7 }}>
              <span style={{ background:'var(--sky-light)', color:'var(--sky)', width:26, height:26, borderRadius:7, display:'inline-flex', alignItems:'center', justifyContent:'center' }}>👤</span>
              Edit Profile
            </div>
            <div className="form-row-2">
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input className="form-input" value={fullName} onChange={e => setFullName(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input className="form-input" value={user?.email} disabled style={{ background:'var(--surface)', color:'var(--text3)' }} />
              </div>
            </div>
            <div className="form-row-2">
              <div className="form-group">
                <label className="form-label">Role</label>
                <input className="form-input" value={ROLE_LABELS[user?.role]} disabled style={{ background:'var(--surface)', color:'var(--text3)' }} />
              </div>
              <div className="form-group">
                <label className="form-label">Branch</label>
                <input className="form-input" value={user?.branch} disabled style={{ background:'var(--surface)', color:'var(--text3)' }} />
              </div>
            </div>
            <button className="btn btn-primary btn-sm" onClick={handleProfileSave} disabled={saving}>
              {saving ? 'Saving…' : '💾 Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* Change Password */}
      <div className="card">
        <div className="card-body">
          <div style={{ fontFamily:'Sora,sans-serif', fontSize:14, fontWeight:600, marginBottom:16, paddingBottom:12, borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:7 }}>
            <span style={{ background:'var(--sky-light)', color:'var(--sky)', width:26, height:26, borderRadius:7, display:'inline-flex', alignItems:'center', justifyContent:'center' }}>🔑</span>
            {mustChange ? 'Set Your New Password' : 'Change Password'}
          </div>
          {[
            { key:'current_password', label: mustChange ? 'Default Password (set by Admin)' : 'Current Password', placeholder:'Enter your current password', showKey:'current' },
            { key:'new_password', label:'New Password', placeholder:'Min 8 chars, uppercase, number', showKey:'new' },
            { key:'confirm_password', label:'Confirm New Password', placeholder:'Re-enter new password', showKey:'confirm' },
          ].map(field => (
            <div className="form-group" key={field.key}>
              <label className="form-label">{field.label}</label>
              <div style={{ position:'relative' }}>
                <input className="form-input"
                  type={showPw[field.showKey] ? 'text' : 'password'}
                  style={{ paddingRight:40 }} placeholder={field.placeholder}
                  value={pwForm[field.key]}
                  onChange={e => setPwForm({ ...pwForm, [field.key]:e.target.value })} />
                <span onClick={() => setShowPw({ ...showPw, [field.showKey]:!showPw[field.showKey] })}
                  style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', cursor:'pointer', fontSize:16, color:'var(--text3)' }}>
                  {showPw[field.showKey] ? '🙈' : '👁️'}
                </span>
              </div>
              {field.key === 'new_password' && <PasswordStrength password={pwForm.new_password} />}
            </div>
          ))}
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', padding:'8px 12px', marginBottom:14, fontSize:12, color:'var(--text3)' }}>
            ⚠️ Your new password must be different from your default/current password.
          </div>
          <button className="btn btn-primary" onClick={handlePasswordChange} disabled={pwSaving}>
            {pwSaving ? 'Saving…' : mustChange ? '🔐 Set New Password & Go to Dashboard' : '🔐 Update Password'}
          </button>
        </div>
      </div>
    </div>
  );
}
